/**
 * English language keywords for category prediction
 */
import type {
  AccommodationSubcategoryKeywords,
  ContextKeywords,
  DrinksConfig,
  DrinksSubcategoryKeywords,
  EntertainmentSubcategoryKeywords,
  FoodConfig,
  FoodSubcategoryKeywords,
  HealthcareSubcategoryKeywords,
  LanguageKeywords,
  MLPatternKeywords,
  ShoppingContextKeywords,
  ShoppingSubcategoryKeywords,
  StemmingConfig,
  TransportConfig,
  TransportKeywords,
  TransportSubcategoryKeywords,
  UtilitiesSubcategoryKeywords,
} from '../types';

// Transport-related keywords for disambiguation
export const transportKeywords: TransportKeywords = {
  general: [
    'train',
    'bus',
    'flight',
    'metro',
    'subway',
    'rail',
    'tram',
    'plane',
    'taxi',
    'uber',
    'lyft',
    'transit',
    'transport',
    'travel',
    'airline',
    'railway',
    'coach',
    'shuttle',
    'ferry',
    'boat',
    'car',
    'ride',
    'trip',
    'journey',
    'commute',
    'airport',
    'station',
  ],
  tickets: ['ticket', 'tickets', 'pass', 'fare'],
  tripContext: [
    'trip',
    'road trip',
    'travel',
    'journey',
    'vacation',
    'drive',
    'driving',
  ],
};

// Transport subcategory keywords
export const transportSubcategories: TransportSubcategoryKeywords = {
  car: [
    'car payment',
    'car loan',
    'auto loan',
    'car wash',
    'car detailing',
    'auto insurance',
  ],
  // EN common words only — brand names (Greyhound, Megabus, FlixBus, etc.)
  // live in `../shared/brands.ts` and merge in for every locale.
  bus: ['bus', 'coach', 'shuttle', 'bus ticket', 'bus pass', 'bus fare'],
  train: [
    'train',
    'rail',
    'railway',
    'train ticket',
    'train pass',
    'rail pass',
    'regional rail',
  ],
  tram: ['streetcar', 'tram', 'trolley', 'light rail', 'tram ticket'],
  flight: [
    'flight',
    'airline',
    'airplane',
    'plane',
    'jet',
    'aviation',
    'flight ticket',
    'plane ticket',
    'airline ticket',
    'boarding pass',
  ],
  bike: [
    'bike',
    'bicycle',
    'electric scooter',
    'e-scooter',
    'cycling',
    'ebike',
    'e-bike',
  ],
  boat: [
    'ferry',
    'boat',
    'ship',
    'cruise',
    'maritime',
    'yacht',
    'vessel',
    'sailing',
    'catamaran',
    'cruise line',
  ],
  parking: [
    'parking',
    'parking lot',
    'parking meter',
    'parking garage',
    'parking fee',
  ],
  taxi: [
    'taxi',
    'cab',
    'taxicab',
    'yellow cab',
    'black cab',
    'taxi ride',
    'taxi fare',
  ],
  rideshare: ['rideshare', 'ride share', 'ride-share', 'carshare'],
  metro: ['metro', 'subway', 'underground', 'tube'],
  fuel: [
    'gas',
    'gasoline',
    'petrol',
    'diesel',
    'fuel',
    'gas station',
    'petrol station',
    'fill up',
    'refuel',
  ],
  toll: ['toll', 'toll road', 'toll booth', 'turnpike', 'toll plaza'],
  rental: ['rental', 'car rental', 'rent a car'],
  maintenance: [
    'car repair',
    'auto repair',
    'car maintenance',
    'auto maintenance',
    'auto service',
    'oil change',
    'tire change',
    'tire rotation',
    'tire repair',
    'brake repair',
    'brakes',
    'transmission',
    'mechanic',
    'auto shop',
  ],
  airport: [
    'airport',
    'airport terminal',
    'airport parking',
    'airport shuttle',
    'airport transfer',
    'airport lounge',
  ],
};

// Only the generic word is shared by food and healthcare: on its own it
// cannot say sports nutrition or health, so the cloud tier breaks that
// tie. Specific products go to one parent so the free tier can answer.
const supplementsKeywords: string[] = ['supplements'];
const sportsNutritionKeywords: string[] = [
  'protein',
  'protein powder',
  'whey',
  'creatine',
  'bcaa',
  'pre workout',
  'pre-workout',
  'preworkout',
];
const healthSupplementsKeywords: string[] = [
  'collagen',
  'vitamins',
  'vitamin',
  'omega 3',
  'omega-3',
  'probiotics',
  'magnesium',
  'multivitamin',
];

// Food subcategory keywords (partial list for demonstration)
export const foodSubcategories: Partial<FoodSubcategoryKeywords> = {
  // EN common words only — brand names + universal food terms live in
  // `../shared/brands.ts`. Item subcats stay before store/venue subcats
  // so "croissant" → pastry (brand) wins on tie over bakery (store).
  // ─── Specific items (win first) ───
  pizza: ['pizza place', 'pizza delivery'],
  sushi: ['japanese'],
  sandwich: ['sub'],
  soup: ['soup', 'stew', 'broth', 'chicken soup', 'tomato soup'],
  salad: [
    'salad',
    'caesar salad',
    'greek salad',
    'garden salad',
    'salad bowl',
    'salad bar',
  ],
  pastry: ['pastry'],
  dessert: ['cake', 'pie', 'cookies', 'tart', 'pudding'],
  candy: ['candy', 'sweets', 'chocolate bar', 'gummies', 'lollipop'],
  icecream: ['ice cream', 'icecream', 'frozen yogurt', 'popsicle'],
  snacks: [
    'snack',
    'snacks',
    'pretzels',
    'trail mix',
    'granola bar',
    'jerky',
    'crackers',
    'peanuts',
    'mixed nuts',
  ],
  fruit: ['orange', 'fruit', 'fruits'],
  vegetables: ['vegetable', 'vegetables', 'veggies', 'greens'],
  meat: ['butcher', 'ground beef'],
  seafood: ['seafood', 'fish'],
  dairy: ['dairy', 'cottage cheese'],
  breakfast: ['breakfast', 'pancakes', 'waffles', 'eggs', 'cereal'],
  // ─── Stores / venues / services (catch-all, last) ───
  restaurant: [
    'restaurant',
    'diner',
    'bistro',
    'italian',
    'chinese',
    'mexican',
    'indian',
    'thai',
    'vietnamese',
    'korean',
    'fast food',
    'lunch',
    'midday meal',
    'lunch break',
    'dinner',
    'supper',
    'evening meal',
    // Ties drinks/alcohol's 'night out' so "Date night out" is not trusted.
    'date night',
  ],
  bakery: ['bakery', 'bread', 'baker', 'bakeshop'],
  cafe: ['cafe', 'café', 'cafeteria', 'coffee shop'],
  delivery: ['delivery', 'takeout', 'takeaway', 'food delivery'],
  groceries: ['groceries', 'supermarket', 'grocery store', 'metro grocery'],
  supplements: [...sportsNutritionKeywords, ...supplementsKeywords],
};

export const drinksSubcategories: DrinksSubcategoryKeywords = {
  // EN common words only — brands live in `../shared/brands.ts`.
  // Bare "water" words stay out: they collide with utilities/water.
  drinks: [
    'beverage',
    'beverages',
    'drink',
    'refreshment',
    'refreshments',
    'hot drinks',
    'cold drinks',
  ],
  water: [
    'bottle of water',
    'water bottle',
    'bottled water',
    'mineral water',
    'sparkling water',
  ],
  juice: ['juice', 'orange juice', 'apple juice', 'juice bar'],
  soda: [
    'soda',
    'coke',
    'cola',
    'soft drink',
    'soft drinks',
    'fizzy drinks',
    'lemonade',
  ],
  coffee: [
    'coffee',
    'iced coffee',
    'americano',
    'flat white',
    'mocha',
    'cold brew',
  ],
  tea: ['tea', 'iced tea', 'green tea', 'kombucha'],
  energy: ['energy drink', 'energy drinks', 'sports drink'],
  beer: ['beers', 'pint', 'pints', 'lager', 'ipa', 'craft beer'],
  wine: [
    'red wine',
    'white wine',
    'glass of wine',
    'bottle of wine',
    'wine shop',
  ],
  cocktails: ['mimosa', 'mimosas'],
  spirits: ['spirits', 'liqueur'],
  // The plural, social "drinks" is an outing for alcohol; the singular
  // stays generic. Bare 'rounds' is left out: golf rounds, 'surrounds'
  // and 'campgrounds' through the substring rule. 'pub crawl' already
  // lands on bar through 'pub'.
  alcohol: [
    'alcohol',
    'liquor',
    'liquor store',
    'booze',
    'drinks',
    'drinks night',
    'drinks out',
    'drinks with',
    'night out',
    'round of drinks',
  ],
  // 'karaoke bar' ties entertainment/events' 'karaoke' for the cloud to break.
  bar: [
    'bar',
    'pub',
    'tavern',
    'brewery',
    'bar tab',
    'happy hour',
    'karaoke bar',
  ],
};

// Context keywords
export const contextKeywords: ContextKeywords = {
  flight: ['baggage'],
  fuel: [
    'trip',
    'road trip',
    'travel',
    'journey',
    'vacation',
    'drive',
    'driving',
    'car',
    'vehicle',
    'miles',
    'kilometers',
  ],
  transportTickets: [
    'ticket',
    'tickets',
    'pass',
    'fare',
    'transit',
    'transportation',
    'transport',
    'commute',
    'journey',
    'travel',
    'trip',
  ],
};

// Entertainment subcategory keywords
const entertainmentSubcategories: EntertainmentSubcategoryKeywords = {
  // EN common words only — brand names + universal entertainment loanwords
  // live in `../shared/brands.ts`.
  music: [
    'concert',
    'concert tickets',
    'music',
    'album',
    'song',
    'artist',
    'rock band',
    'music band',
    'vinyl',
  ],
  streaming: ['subscription'],
  gaming: ['game', 'videogame'],
  books: [
    'book',
    'magazine',
    'newspaper',
    'library',
    'reading',
    'novel',
    'literature',
    'bookstore',
  ],
  fitness: ['gym', 'fitness', 'workout', 'personal trainer'],
  movies: [
    'movie',
    'cinema',
    'screening',
    'movie tickets',
    'movie theater',
    'matinee',
    // These (and 'bowling', 'karaoke') tie with drinks/alcohol's 'night out'
    // so "Movie night out" goes to the cloud instead of trusted alcohol.
    'movies',
    'movie night',
    'night at the movies',
  ],
  events: [
    'concert',
    'live music',
    'music festival',
    'live gig',
    'music gig',
    'performance',
    'museum',
    'gallery',
    'art gallery',
    'exhibition',
    'cultural center',
    'event',
    'conference',
    'convention',
    'meetup',
    'gathering',
    'festival',
    'fair',
    'carnival',
    'celebration',
    'fiesta',
    'theater',
    'theatre',
    'play',
    'musical',
    'opera',
    'nightlife',
    'club',
    'lounge',
    'nightclub',
    'karaoke',
    'karaoke night',
    'ticket',
    'tickets',
    'show',
    'exhibition theater',
  ],
  outdoor: [
    'park',
    'amusement park',
    'theme park',
    'zoo',
    'aquarium',
    'hiking',
    'camping',
    'fishing',
    'hunting',
    'outdoor',
    'adventure',
    'nature',
    'trail',
    'mountain',
    'climbing',
    'trekking',
    'backpacking',
    'rock climbing',
    'mountaineering',
    'wildlife',
  ],
  sports: [
    'baseball',
    'football',
    'soccer',
    'basketball',
    'tennis',
    'hockey',
    'golf',
    'bowling',
    'bowling night',
    'stadium',
    'arena',
    'sports game',
    'sporting event',
  ],
  photography: [
    'camera',
    'photo',
    'photography',
    'lens',
    'photoshoot',
    'photographer',
  ],
  podcasts: [
    'podcast',
    'radio',
    'talk show',
    'podcast episode',
    'podcast app',
    'radio bill',
  ],
  water: [
    'scuba',
    'diving',
    'beach',
    'swimming',
    'water sports',
    'sailing',
    'boating',
    'water ski',
    'aquatic',
    'marine',
  ],
};

// Shopping subcategory keywords
const shoppingSubcategories: ShoppingSubcategoryKeywords = {
  // EN common words only — brand names live in `../shared/brands.ts`.
  clothes: [
    'clothes',
    'clothing',
    'shirt',
    'pants',
    'dress',
    'jacket',
    'apparel',
    'fashion',
    'outfit',
    'wardrobe',
    'garment',
  ],
  electronics: [
    'electronics',
    'laptop',
    'phone',
    'computer',
    'tablet',
    'headphones',
    'gadget',
    'appliances',
    'refrigerator',
    'washer',
    'dryer',
    'dishwasher',
    'microwave',
    'oven',
    'coffee machine',
    'coffee maker',
  ],
  groceries: ['groceries', 'supermarket', 'food shopping'],
  online: ['online', 'online shopping', 'ecommerce', 'shipping'],
  furniture: [
    'furniture',
    'chair',
    'table',
    'sofa',
    'bed',
    'desk',
    'decoration',
    'home decor',
    'household',
    'home improvement',
    'interior',
  ],
  gifts: [
    'gift',
    'present',
    'birthday',
    'christmas',
    'anniversary',
    'flowers',
    'greeting card',
    'gift card',
  ],
  services: [
    'plumber',
    'electrician',
    'handyman',
    'contractor',
    'repair',
    'maintenance',
    'service',
    'technician',
    'painter',
    'carpenter',
    'locksmith',
    'cleaning',
    'installation',
    'hvac',
    'appliance repair',
  ],
  shoes: ['shoes', 'footwear', 'sneakers', 'boots', 'sandals'],
  books: ['book', 'books', 'bookstore', 'magazine'],
  toys: ['toys', 'games', 'toy store'],
  mall: [
    'mall',
    'shopping mall',
    'shopping center',
    'shopping centre',
    'outlet mall',
    'outlet',
    'town center',
    'retail park',
  ],
  market: ['farmers market', 'flea market', "farmers' market", 'street market'],
  department: ['department store'],
  jewelry: [
    'jewelry',
    'jewellery',
    'ring',
    'necklace',
    'bracelet',
    'watch',
    'accessories',
    'bags',
    'purse',
    'wallet',
    'belt',
    'scarf',
    'hat',
  ],
  cosmetics: [
    'cosmetics',
    'makeup',
    'beauty',
    'perfume',
    'skincare',
    'haircare',
    'salon',
    'spa',
  ],
  sports: [
    'sports',
    'sporting',
    'sporting goods',
    'sports equipment',
    'gym equipment',
    'athletic',
  ],
  hardware: ['hardware', 'hardware store', 'tools'],
  garden: [
    'garden',
    'garden center',
    'plants',
    'flowers',
    'gardening',
    'outdoor furniture',
    'patio',
    'lawn',
  ],
};

// Accommodation subcategory keywords
const accommodationSubcategories: AccommodationSubcategoryKeywords = {
  // EN common words only — brand chains + booking platforms live in
  // `../shared/brands.ts`.
  hotel: [
    'hotel',
    'motel',
    'inn',
    'resort',
    'lodge',
    'suite',
    'beach resort',
    'ski resort',
    'spa resort',
    'all inclusive',
    'motor inn',
    'roadside inn',
    'budget motel',
    'room',
    'bedroom',
    'lodging',
  ],
  airbnb: ['vacation rental', 'holiday home', 'short term rental', 'homestay'],
  apartment: [
    'apartment',
    'flat',
    'condo',
    'condominium',
    'studio',
    'loft',
    'penthouse',
    'duplex',
  ],
  house: [
    'house',
    'home',
    'residence',
    'villa',
    'cottage',
    'bungalow',
    'townhouse',
  ],
  rent: [
    'rent',
    'lease',
    'rental',
    'landlord',
    'tenant',
    'monthly rent',
    'rent payment',
  ],
  hostel: [
    'hostel',
    'backpacker',
    'dorm',
    'dormitory',
    'budget accommodation',
    'youth hostel',
  ],
  camping: [
    'camping',
    'campground',
    'tent',
    'rv park',
    'caravan',
    'campsite',
    'glamping',
  ],
};

// Healthcare subcategory keywords
const healthcareSubcategories: HealthcareSubcategoryKeywords = {
  doctor: [
    'doctor',
    'physician',
    'clinic',
    'checkup',
    'general practitioner',
    'gp',
    'primary care',
    'doctor visit',
  ],
  // EN common words only — brands live in `../shared/brands.ts`.
  pharmacy: ['pharmacy', 'drugstore', 'medication', 'prescription', 'medicine'],
  hospital: [
    'hospital',
    'emergency',
    'er',
    'urgent care',
    'emergency room',
    'surgery',
    'operation',
    'ambulance',
    'lab',
    'laboratory',
    'blood test',
    'lab work',
    'medical test',
    'diagnostic',
  ],
  dental: [
    'dental',
    'dentist',
    'teeth',
    'orthodontist',
    'tooth',
    'oral',
    'cleaning',
    'braces',
    'dentist appointment',
    'dental appointment',
  ],
  vision: [
    'vision',
    'eye',
    'optometrist',
    'glasses',
    'contacts',
    'contact lenses',
    'lenses',
    'eye exam',
    'ophthalmologist',
  ],
  specialist: [
    'specialist',
    'specialist consultation',
    'specialist appointment',
    'specialist visit',
    'dermatologist',
    'cardiologist',
    'pediatrician',
    'psychiatrist',
    'therapy',
    'mental health',
    'psychologist',
    'counseling',
    'therapy session',
    'physical therapy',
    'rehabilitation',
    'physiotherapy',
    'occupational therapy',
  ],
  insurance: [
    'insurance',
    'health insurance',
    'medical insurance',
    'copay',
    'deductible',
    'coverage',
    'claim',
    'insurance premium',
    'health plan',
  ],
  fitness: [
    'gym',
    'gym membership',
    'fitness',
    'fitness center',
    'health club',
    'workout',
    'exercise',
    'personal trainer',
    'training',
    'wellness',
    'wellness center',
    'holistic',
    'alternative medicine',
    'acupuncture',
    'massage',
  ],
  supplements: [...healthSupplementsKeywords, ...supplementsKeywords],
};

// Utilities subcategory keywords
const utilitiesSubcategories: UtilitiesSubcategoryKeywords = {
  // EN common words only — brand providers live in `../shared/brands.ts`.
  electricity: [
    'electricity',
    'electric',
    'power',
    'energy',
    'utility bill',
    'electric company',
    'kilowatt',
  ],
  water: [
    'water',
    'water bill',
    'water service',
    'water utility',
    'sewer',
    'drainage',
  ],
  gas: [
    'gas',
    'natural gas',
    'gas bill',
    'gas utility bill',
    'gas utility',
    'gas service',
    'heating',
    'propane',
    'heat',
    'furnace',
    'boiler',
    'radiator',
    'hvac',
  ],
  internet: ['internet', 'wifi', 'broadband', 'fiber', 'cable', 'isp'],
  phone: [
    'phone',
    'mobile',
    'cellular',
    'cell phone',
    'telephone',
    'phone bill',
    'data plan',
  ],
  trash: ['trash', 'garbage', 'waste', 'recycling', 'sanitation', 'disposal'],
  maintenance: [
    'maintenance',
    'hoa',
    'condo fee',
    'building maintenance',
    'building upkeep',
    'upkeep',
    'repairs',
    'property management',
    'property repair',
    'home maintenance',
  ],
  streaming: [
    'streaming',
    'subscription',
    'streaming service',
    'cable tv',
    'cable bill',
    'tv subscription',
    'tv bill',
    'television',
    'satellite',
  ],
};

// Shopping context keywords for better subcategory detection
const shoppingContext: ShoppingContextKeywords = {
  clothes: ['fashion', 'wear', 'retail'],
  electronics: ['tech', 'device', 'gadget'],
  furniture: ['showroom', 'home decor', 'interior'],
  online: ['order', 'shipping', 'tracking'],
  groceries: ['weekly', 'monthly', 'shopping list'],
  services: ['fix', 'repair', 'broken', 'install', 'maintenance'],
};

// ML model pattern keywords for fast matching
const mlPatterns: MLPatternKeywords = {
  expense: {
    transport: [
      'uber',
      'lyft',
      'taxi',
      'bus',
      'train',
      'flight',
      'gas',
      'parking',
      'metro',
      'subway',
      'vacation',
      'travel',
      'trip',
    ],
    food: [
      'restaurant',
      'lunch',
      'dinner',
      'grocery',
      'coffee',
      'pizza',
      'food',
      'breakfast',
    ],
    entertainment: ['netflix', 'spotify', 'movie', 'concert', 'game', 'gym'],
    shopping: ['amazon', 'target', 'walmart', 'clothes', 'shopping'],
    healthcare: ['doctor', 'hospital', 'pharmacy', 'dentist', 'medical'],
    utilities: ['electricity', 'water', 'internet', 'phone', 'bill'],
    accommodation: ['hotel', 'airbnb', 'rent', 'apartment'],
    education: [
      'course',
      'class',
      'school',
      'university',
      'college',
      'book',
      'textbook',
      'udemy',
      'coursera',
      'training',
      'certification',
      'workshop',
      'seminar',
      'tuition',
      'learning',
      'study',
    ],
  },
  group: {
    friends: [
      'friends',
      'friendship',
      'buddies',
      'pals',
      'crew',
      'gang',
      'social',
      'hangout',
      'group',
      'circle',
      'squad',
      'companions',
      'dinner',
      'game',
      'night',
      'weekend',
      'dinner party',
      'game night',
    ],
    family: [
      'family',
      'parents',
      'siblings',
      'relatives',
      'reunion',
      'grandparents',
      'children',
      'cousins',
      'extended',
      'household',
      'kinship',
      'heritage',
      'vacation',
      'holiday',
      'meal',
      'groceries',
      'grocery',
      'shared',
    ],
    couple: [
      'couple',
      'boyfriend',
      'girlfriend',
      'partner',
      'spouse',
      'husband',
      'wife',
      'relationship',
      'romantic',
      'dating',
      'love',
      'together',
      'date',
      'night',
      'bill',
      'bills',
      'apartment',
      'expense',
      'expenses',
    ],
    events: [
      'party',
      'parties',
      'wedding',
      'birthday',
      'celebration',
      'anniversary',
      'graduation',
      'concert',
      'festival',
      'event',
      'ceremony',
      'reception',
      'gathering',
      'special',
      'milestone',
      'baby',
      'shower',
      'bridal',
    ],
    roommates: [
      'roommates',
      'roommate',
      'apartment',
      'house',
      'rent',
      'lease',
      'shared',
      'living',
      'housemates',
      'flatmates',
      'rental',
      'dwelling',
      'residence',
      'utilities',
      'utility',
      'household',
      'supplies',
      'electricity',
      'water',
      'internet',
      'cleaning',
    ],
    trip: [
      'travel',
      'trip',
      'vacation',
      'holiday',
      'honeymoon',
      'cruise',
      'tour',
      'getaway',
      'adventure',
      'journey',
      'destination',
      'explore',
      'backpack',
      'sightseeing',
      'hotel',
      'hotels',
      'flight',
      'flights',
      'meal',
      'meals',
      'restaurant',
      'dining',
    ],
    work: [
      'work',
      'office',
      'project',
      'team',
      'company',
      'business',
      'startup',
      'conference',
      'meeting',
      'corporate',
      'colleagues',
      'professional',
      'department',
      'workplace',
      'lunch',
      'lunches',
      'supplies',
      'office supplies',
      'events',
      'event',
    ],
  },
};

// Food configuration
const foodConfig: FoodConfig = {
  obviousSubcategories: ['fruit', 'vegetables'],
  subcategoryPriority: ['pizza', 'hamburger', 'sandwich', 'soup', 'cafe'],
};

const drinksConfig: DrinksConfig = {
  subcategoryPriority: [
    'cocktails',
    'beer',
    'wine',
    'spirits',
    'energy',
    'coffee',
    'tea',
    'juice',
    'soda',
    'water',
    'bar',
    'alcohol',
    'drinks',
  ],
};

// Transport configuration
//
// Priority order is the tie-breaker the generic detector uses when
// multiple subcategories score within 0.1 of each other
// (see `subcategoryDetection.ts`). It matters for inputs that
// mention both a mode and a context word — e.g. "uber airport":
//   uber  -> rideshare (0.8), car (0.8)
//   airport -> airport (0.9, gets the +0.1 length bonus)
// All three land in the similar-scores window, and priority decides
// the winner. `airport` is intentionally placed below the concrete
// transport modes because it usually appears as the *destination*
// (e.g. "uber airport", "taxi to the airport"), not the actual
// expense type. Keeping it above modes caused "uber airport" to
// resolve to `airport` instead of `rideshare`.
const transportConfig: TransportConfig = {
  subcategoryPriority: [
    'rental',
    'flight',
    'taxi',
    'rideshare',
    'car',
    'bike',
    'boat',
    'train',
    'tram',
    'airport',
  ],
};

// English stemming configuration
const stemming: StemmingConfig = {
  suffixes: ['ing', 'ed', 's', 'es', 'ly', 'er', 'est', 'tion', 'ment'],
};

// Complete English language keyword configuration
export const enKeywords: LanguageKeywords = {
  transport: transportKeywords,
  transportSubcategories,
  foodSubcategories: foodSubcategories as FoodSubcategoryKeywords,
  drinksSubcategories,
  entertainmentSubcategories,
  shoppingSubcategories,
  accommodationSubcategories,
  healthcareSubcategories,
  utilitiesSubcategories,
  context: contextKeywords,
  shoppingContext,
  foodConfig,
  drinksConfig,
  transportConfig,
  mlPatterns,
  stemming,
};
